import React, { useEffect, useRef, useState } from 'react';
import { Link, useHistory, useParams } from 'react-router-dom';
import PropTypes from 'prop-types';

import {
  isEmptyCollection,
  SiteConfigManager,
  SportManager,
  useSportsbookState,
} from '@atlas/core';
import { COMPONENT_TYPES, sportsbookRoutes } from '@atlas/routing';
import { Button, ChipsItem, Label, Loader } from '@atlas/ui';

import MarketTypeSelector from 'common/MarketTypeSelector/MarketTypeSelector';
import initialTournamentShape from 'common/PropTypes/initialTournamentShape';
import { defaultRoutes } from 'components/MainRouter/constants';
import PrematchSportsbookLoader from 'components/Pages/PrematchSportsbookLoader';
import { useSelectionsContext } from 'components/SportsbookSelections/SelectionsContext';
import SelectionTournaments from 'components/SportsbookSelections/SelectionTournaments';
import useSelections from 'components/SportsbookSelections/useSelections';

const SportsbookSelectionsLoader = ({ children, loading, tournaments }) => {
  if (loading) {
    return (
      <div className="empty-state-wrapper with-header">
        <Loader className="absolute" />
      </div>
    );
  }

  if (isEmptyCollection(tournaments)) {
    const { sportLive, sportPrematch } = defaultRoutes;

    return (
      <div className="empty-state-wrapper with-header">
        <div className="empty-state-text">
          <Label className="title-text" message="this_selection_is_empty_message" />
          <Label className="sub-title-text" message="this_selection_is_empty_description" />
          <div className="sticky-bottom-buttons-container column button-no-markets">
            <Link aria-label="Prematch" to={sportPrematch}>
              <Button className="btn big secondary " label="go_to_sport" />
            </Link>

            <Link aria-label="Live" to={sportLive}>
              <Button className="btn big primary" label="go_to_live" />
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return children;
};

SportsbookSelectionsLoader.propTypes = {
  loading: PropTypes.bool,
  tournaments: PropTypes.arrayOf(initialTournamentShape).isRequired,
};

const SportsbookSelections = () => {
  const { selectionId: selectedSelectionId } = useParams();
  const { filter, loading: isSportsLoading, sports } = useSportsbookState();
  const { timeHours: timeHoursFilter } = filter;

  const { isSelectionsLoading, selections, tournaments } = useSelections({
    selectedSelectionId,
  });
  const { isLoading } = useSelectionsContext();

  const previousSelectionId = useRef(selectedSelectionId);

  const { push: historyPush } = useHistory();
  const [loading, setLoading] = useState(true);
  const [tournamentsData, setTournamentsData] = useState([]);

  const isLoadingView = isLoading || loading || isSelectionsLoading;

  const sportPrematchPath = SiteConfigManager.getComponentPath(COMPONENT_TYPES.SPORT_PREMATCH);
  const mainMarketsChoice = !!SiteConfigManager.getConfig(
    'config.config.sportsbook.mainMarketsChoice',
  );

  useEffect(() => {
    if (
      !isSportsLoading &&
      sports.length &&
      ((!isSelectionsLoading && isEmptyCollection(selections)) || timeHoursFilter)
    ) {
      historyPush(`${sportPrematchPath}/${sports[0].urlName}`);
    }
  }, [
    historyPush,
    isSelectionsLoading,
    isSportsLoading,
    selections,
    sportPrematchPath,
    sports,
    timeHoursFilter,
  ]);

  useEffect(() => {
    if (!tournaments || !tournaments.length || !selectedSelectionId) {
      setTournamentsData([]);
      return;
    }
    setLoading(true);
    SportManager.findTournaments({ filter: { tournamentIds: tournaments.map(value => value.id) } })
      .then(({ data: tournamentsData }) => {
        const preparedTournaments = tournaments.reduce((acc, tournament, index) => {
          acc[index] = tournament;
          const tournamentData = tournamentsData.find(value => value.id === tournament.id);
          acc[index] = {
            ...acc[index],
            imagePaths: tournamentData?.imagePaths,
            sport: sports.find(sport => sport.id === tournament.sportId),
            sportFormMainTypes: tournamentData?.sportForm && tournamentData.sportForm,
          };
          return acc;
        }, []);

        setTournamentsData(preparedTournaments);
      })
      .catch(() => {
        setLoading(false);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [tournaments]);

  useEffect(() => {
    if (selections.length && !selectedSelectionId) {
      historyPush(`${sportPrematchPath}/${sportsbookRoutes.selections}/${selections[0]?.id}`);
    }
  }, [historyPush, selectedSelectionId, selections, sportPrematchPath]);

  useEffect(() => {
    if (previousSelectionId.current !== selectedSelectionId) {
      setLoading(true);
      previousSelectionId.current = selectedSelectionId;
    }
  }, [selectedSelectionId]);

  return (
    <PrematchSportsbookLoader>
      <div className="history-filter">
        <div className="chips-list">
          {selections
            .sort((a, b) => a.orderPosition - b.orderPosition)
            .map(({ id, name }) => {
              return (
                <Link key={id} to={`${id}`}>
                  <ChipsItem active={Number(selectedSelectionId) === id} key={id} label={name} />
                </Link>
              );
            })}
        </div>
      </div>

      <SportsbookSelectionsLoader loading={isLoadingView} tournaments={tournamentsData}>
        {mainMarketsChoice && <MarketTypeSelector />}

        <div className="selections-tournaments">
          <SelectionTournaments tournaments={tournamentsData} />
        </div>
      </SportsbookSelectionsLoader>
    </PrematchSportsbookLoader>
  );
};

export default SportsbookSelections;
